import { useEffect, useState } from "react";
import { GitCompareArrows, Loader2, MessageSquareText } from "lucide-react";
import { api } from "@/api/client";
import type { RunDiff, StudyRun } from "../../../types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { cn } from "@/lib/utils";

function similarityTone(value: number): { label: string; className: string } {
  if (value >= 0.7) return { label: "stable", className: "text-green-600" };
  if (value >= 0.4) return { label: "shifting", className: "text-amber-600" };
  return { label: "volatile", className: "text-red-500" };
}

interface ConsistencyPanelProps {
  studyId: number;
  runs: StudyRun[];
  /** Bumped by the parent when a run completes, to refetch diffs. */
  refreshKey: number;
}

/**
 * "Are we getting the same answers back?" — per-run similarity vs the previous
 * run, structural change flags, and an on-demand LLM explanation of what changed.
 */
export function ConsistencyPanel({ studyId, runs, refreshKey }: ConsistencyPanelProps) {
  const [diffs, setDiffs] = useState<RunDiff[]>([]);
  const [explaining, setExplaining] = useState<number | null>(null); // run_id in flight
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    api.studies.diffs(studyId)
      .then(d => { if (!cancelled) setDiffs(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [studyId, refreshKey]);

  if (diffs.length === 0) return null;

  const runNumberById = new Map(runs.map(r => [r.id, r.run_number]));

  const handleExplain = async (diff: RunDiff) => {
    setExplaining(diff.run_id);
    setError("");
    try {
      const { explanation } = await api.studies.explainRun(diff.run_id);
      setDiffs(prev => prev.map(d => (d.id === diff.id ? { ...d, explanation } : d)));
    } catch (err: any) {
      setError(err?.message || "Failed to analyze the change");
    } finally {
      setExplaining(null);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <GitCompareArrows className="w-4 h-4 text-brand-600" />
          Answer Consistency
        </CardTitle>
        <CardDescription>How much each run's answer changed from the run before it</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="divide-y divide-slate-100">
          {[...diffs].reverse().map(diff => {
            const tone = diff.similarity !== null ? similarityTone(diff.similarity) : null;
            const hasFlags = diff.winner_changed || diff.added_brands.length > 0 || diff.dropped_brands.length > 0;
            return (
              <div key={diff.id} className="py-3 first:pt-0 last:pb-0">
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
                  <span className="text-[12px] font-semibold text-slate-400 tabular-nums shrink-0">
                    #{runNumberById.get(diff.prev_run_id) ?? "?"} → #{runNumberById.get(diff.run_id) ?? "?"}
                  </span>

                  {diff.similarity !== null && tone && (
                    <span className={cn("text-[13px] font-semibold tabular-nums", tone.className)}>
                      {(diff.similarity * 100).toFixed(0)}% similar
                      <span className="ml-1.5 text-[11px] font-medium opacity-80">({tone.label})</span>
                    </span>
                  )}

                  {diff.winner_changed && (
                    <Badge variant="warning">
                      winner: {diff.prev_winner || "none"} → {diff.winner || "none"}
                    </Badge>
                  )}
                  {diff.added_brands.map(b => <Badge key={`a-${b}`} variant="success">+ {b}</Badge>)}
                  {diff.dropped_brands.map(b => <Badge key={`d-${b}`} variant="error">− {b}</Badge>)}
                  {!hasFlags && diff.similarity !== null && diff.similarity >= 0.7 && (
                    <span className="text-[12px] text-slate-400">no material changes</span>
                  )}

                  {diff.similarity !== null && !diff.explanation && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="ml-auto shrink-0"
                      onClick={() => handleExplain(diff)}
                      disabled={explaining !== null}
                    >
                      {explaining === diff.run_id
                        ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                        : <MessageSquareText className="w-3.5 h-3.5 mr-1.5" />}
                      What changed?
                    </Button>
                  )}
                </div>

                {diff.explanation && (
                  <p className="mt-2 text-[13px] text-slate-600 leading-relaxed bg-slate-50 rounded-md px-3 py-2.5">
                    {diff.explanation}
                  </p>
                )}
              </div>
            );
          })}
        </div>
        {error && <p className="mt-3 text-[12px] text-red-600">{error}</p>}
      </CardContent>
    </Card>
  );
}
